import { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Button, Table, Alert, Row, Col, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getDisplaySerieFolio } from '../../utils/invoiceDisplay'
import { FORMA_PAGO_SAT_OPTIONS, paymentMethodToFormaPago } from '../../constants/formaPagoSat'
import { extractApiErrorMessage } from '../../utils/apiErrors'
import { formatCurrency } from '../../utils/formatters'
import { preseleccionar } from '../../utils/configuracionComplemento'

const ComplementoPagoModal = ({ show, onHide, invoice, onSuccess }) => {
  const queryClient = useQueryClient()
  const [selectedAplicacionIds, setSelectedAplicacionIds] = useState([])
  const [lineasById, setLineasById] = useState({})
  const [pagosById, setPagosById] = useState({})
  const [formaPago, setFormaPago] = useState('03')
  const [fechaPago, setFechaPago] = useState('')
  const [referencia, setReferencia] = useState('')
  const [configuracionId, setConfiguracionId] = useState('')

  const {
    data: pendientesData,
    isLoading: loadingPendientes,
    isError: errorPendientes,
    error: pendientesError,
  } = useQuery(
    ['aplicaciones-pendientes-complemento', invoice?.id],
    () => api.get(`/financial/invoices/${invoice.id}/aplicaciones-pendientes-complemento/`).then((r) => r.data),
    { enabled: !!show && !!invoice?.id, retry: false }
  )

  const clientId = pendientesData?.factura?.client_id || invoice?.client || invoice?.client_id

  const { data: configuraciones } = useQuery(
    ['facturacion-config-complemento', clientId],
    async () => {
      if (!clientId) return []
      const response = await api.get(
        `/financial/configuraciones-facturacion/?cliente_id=${clientId}&activo=true`
      )
      const list = response.data.results || response.data || []
      return list.filter(
        (c) => c.activo !== false && (c.tipos_permitidos || []).includes('complemento')
      )
    },
    { enabled: !!show && !!clientId }
  )

  const { data: catalogoMonedas } = useQuery(
    'catalogo-monedas-sat-rep',
    async () => {
      const response = await api.get('/financial/invoices/catalogo-moneda/')
      return response.data.results || response.data || []
    },
    { enabled: !!show, staleTime: 600000 }
  )

  // useMemo porque `|| []` creaba un array nuevo en cada render y hacia
  // recalcular los useMemo que dependen de el.
  const aplicaciones = useMemo(() => pendientesData?.aplicaciones || [], [pendientesData])
  const complementoBorradorId = pendientesData?.complemento_borrador_id
  const monedaDr = (pendientesData?.factura?.moneda || invoice?.moneda || 'MXN').toUpperCase()

  useEffect(() => {
    if (!show || !aplicaciones.length) return
    const ids = aplicaciones.map((a) => a.id)
    setSelectedAplicacionIds(ids)
    const initialLineas = {}
    const initialPagos = {}
    aplicaciones.forEach((a) => {
      initialLineas[a.id] = {
        num_parcialidad: a.num_parcialidad_sugerida ?? 1,
        imp_saldo_ant: a.imp_saldo_ant,
        imp_pagado: a.imp_pagado,
        imp_saldo_insoluto: a.imp_saldo_insoluto,
        equivalencia_dr: a.equivalencia_dr ?? a.equivalencia_dr_sugerida ?? '1',
      }
      const pid = a.pago?.id
      if (pid && !initialPagos[pid]) {
        initialPagos[pid] = {
          moneda: (a.pago.moneda || 'MXN').toUpperCase(),
          tipo_cambio: a.pago.tipo_cambio || '',
        }
      }
    })
    setLineasById(initialLineas)
    setPagosById(initialPagos)
    const pago = aplicaciones[0]?.pago
    if (pago) {
      setFormaPago(
        pago.forma_pago_sugerida
        || (pago.forma_pago && pago.forma_pago !== '99' ? pago.forma_pago : null)
        || paymentMethodToFormaPago(pago.payment_method)
        || '03'
      )
      setFechaPago((pago.payment_date || '').slice(0, 10))
      setReferencia(pago.reference || '')
    }
  }, [show, pendientesData, aplicaciones])

  useEffect(() => {
    if (configuracionId) return
    // La lógica vive en `utils/configuracionComplemento` para poder probarla: dentro del
    // render el primer ciclo corre sin datos y un test del DOM pasaría aunque esté rota.
    const elegida = preseleccionar(configuraciones)
    if (elegida) setConfiguracionId(elegida)
  }, [configuraciones, configuracionId])

  const selectedApps = useMemo(
    () => aplicaciones.filter((a) => selectedAplicacionIds.includes(a.id)),
    [aplicaciones, selectedAplicacionIds]
  )

  const paymentIds = useMemo(() => {
    const ids = [...new Set(selectedApps.map((a) => a.pago?.id).filter(Boolean))]
    return ids
  }, [selectedApps])

  const esMultiPago = paymentIds.length > 1

  const createMutation = useMutation(
    async () => {
      const lineas_detalle = selectedAplicacionIds.map((id) => ({
        id,
        num_parcialidad: lineasById[id]?.num_parcialidad ?? 1,
        imp_saldo_ant: lineasById[id]?.imp_saldo_ant,
        imp_saldo_insoluto: lineasById[id]?.imp_saldo_insoluto,
        equivalencia_dr: lineasById[id]?.equivalencia_dr,
      }))
      const pagos_detalle = paymentIds.map((pid) => ({
        id: pid,
        moneda: pagosById[pid]?.moneda || 'MXN',
        tipo_cambio: (pagosById[pid]?.moneda || 'MXN') === 'MXN' ? null : pagosById[pid]?.tipo_cambio,
      }))
      const payload = {
        aplicacion_ids: selectedAplicacionIds,
        payment_ids: paymentIds,
        configuracion_id: parseInt(configuracionId, 10),
        factura_ppd_id: invoice.id,
        lineas_detalle,
        pagos_detalle,
      }
      if (!esMultiPago) {
        payload.payment_id = paymentIds[0] || null
        payload.forma_pago = formaPago
        payload.fecha_pago = fechaPago
        payload.referencia = referencia
      }
      const res = await api.post('/financial/invoices/crear-complemento-pago/', payload)
      return res.data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries(['invoice', invoice.id])
        toast.success(
          esMultiPago
            ? 'Complemento con varios nodos Pago creado en borrador. Timbrar desde el detalle.'
            : 'Complemento de pago creado en borrador. Timbrar desde el detalle del complemento.'
        )
        resetLocalState()
        onSuccess?.(data)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al crear complemento de pago')
      },
    }
  )

  const resetLocalState = () => {
    setSelectedAplicacionIds([])
    setLineasById({})
    setPagosById({})
    setConfiguracionId('')
  }

  const handleClose = () => {
    resetLocalState()
    onHide()
  }

  const toggleAplicacion = (id, checked) => {
    if (checked) {
      setSelectedAplicacionIds((prev) => [...prev, id])
    } else {
      setSelectedAplicacionIds((prev) => prev.filter((x) => x !== id))
    }
  }

  const updateLinea = (id, field, value) => {
    setLineasById((prev) => {
      const next = { ...prev, [id]: { ...prev[id], [field]: value } }
      if (field === 'imp_saldo_ant' || field === 'imp_pagado') {
        const ant = parseFloat(field === 'imp_saldo_ant' ? value : next[id].imp_saldo_ant) || 0
        const pag = parseFloat(field === 'imp_pagado' ? value : next[id].imp_pagado) || 0
        next[id].imp_saldo_insoluto = Math.max(0, ant - pag).toFixed(2)
      }
      return next
    })
  }

  const updatePago = (pid, field, value) => {
    setPagosById((prev) => {
      const cur = { ...(prev[pid] || { moneda: 'MXN', tipo_cambio: '' }) }
      if (field === 'moneda') {
        const mon = (value || 'MXN').toUpperCase()
        cur.moneda = mon
        if (mon === 'MXN') cur.tipo_cambio = ''
      } else {
        cur[field] = value
      }
      return { ...prev, [pid]: cur }
    })
  }

  const pagosMonedaInvalidos = paymentIds.some((pid) => {
    const mon = (pagosById[pid]?.moneda || 'MXN').toUpperCase()
    if (mon === 'MXN') return false
    return !(parseFloat(pagosById[pid]?.tipo_cambio) > 0)
  })

  const canSubmit =
    selectedAplicacionIds.length > 0 &&
    paymentIds.length > 0 &&
    configuracionId &&
    !pagosMonedaInvalidos &&
    (esMultiPago || (formaPago && fechaPago))

  if (!invoice) return null

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Complemento de pago — Factura {getDisplaySerieFolio(invoice.invoice_number)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="py-2">
          <small>
            CFDI tipo P: Moneda XXX, totales 0, UsoCFDI CP01. MonedaP del cobro; EquivalenciaDR se
            sugiere y puede editarse. TipoCambioP = 1 si MonedaP = MXN (requerido por el PAC). Totales del complemento en MXN.
          </small>
        </Alert>

        {esMultiPago && (
          <Alert variant="warning" className="py-2">
            <small>
              Seleccionó aplicaciones de <strong>{paymentIds.length} pagos</strong>. Se emitirá un solo
              CFDI con un nodo Pago por cada cobro (fecha y forma tomadas de cada pago).
            </small>
          </Alert>
        )}

        {complementoBorradorId && (
          <Alert variant="warning">
            Ya existe un complemento en borrador (#{complementoBorradorId}). Ábralo desde la lista de facturas tipo
            Complemento para editarlo y timbrarlo.
          </Alert>
        )}

        {loadingPendientes ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : errorPendientes ? (
          <Alert variant="danger" className="mb-0">
            {extractApiErrorMessage(pendientesError, 'No se pudieron cargar las aplicaciones de pago.')}
          </Alert>
        ) : aplicaciones.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            {complementoBorradorId
              ? 'Las aplicaciones ya están en el complemento en borrador. Ábralo desde la lista de facturas tipo Complemento para timbrarlo.'
              : 'No hay aplicaciones de pago pendientes de documentar en complemento para esta factura. Verifique que el pago se haya aplicado correctamente a esta factura PPD.'}
          </Alert>
        ) : (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-1">
                  <strong>Receptor:</strong> {pendientesData?.factura?.receptor_razon_social || invoice.receptor_name}
                </p>
                <p className="mb-1 text-muted small">
                  UUID PPD: {pendientesData?.factura?.cfdi_uuid || invoice.cfdi_uuid}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-1">
                  <strong>Total factura ({monedaDr}):</strong>{' '}
                  {formatCurrency(pendientesData?.factura?.total || invoice.total || 0)}
                </p>
              </Col>
            </Row>

            {!esMultiPago && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Forma de pago (nodo Pago) *</Form.Label>
                    <Form.Select value={formaPago} onChange={(e) => setFormaPago(e.target.value)} required>
                      {FORMA_PAGO_SAT_OPTIONS.filter((o) => o.value !== '99').map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Fecha de pago *</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaPago}
                      onChange={(e) => setFechaPago(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Referencia / NumOperacion</Form.Label>
                    <Form.Control
                      type="text"
                      value={referencia}
                      onChange={(e) => setReferencia(e.target.value)}
                      placeholder="Núm. operación, transferencia…"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Serie / configuración de timbrado *</Form.Label>
                  <Form.Select
                    value={configuracionId}
                    onChange={(e) => setConfiguracionId(e.target.value)}
                    required
                  >
                    <option value="">Seleccionar…</option>
                    {configuraciones?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.serie ? `Serie ${c.serie}` : 'Sin serie'} — {c.descripcion || `Config #${c.id}`}
                      </option>
                    ))}
                  </Form.Select>
                  {configuraciones?.length === 0 && (
                    <Form.Text className="text-danger">
                      No hay configuración de facturación que permita complemento de pago.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Label className="mb-2">Moneda de cada cobro (MonedaP) — editable en borrador</Form.Label>
            <Table size="sm" bordered responsive className="mb-3">
              <thead>
                <tr>
                  <th>Pago</th>
                  <th>MonedaP</th>
                  <th>TipoCambioP</th>
                </tr>
              </thead>
              <tbody>
                {paymentIds.map((pid) => {
                  const p = pagosById[pid] || { moneda: 'MXN', tipo_cambio: '' }
                  const mon = (p.moneda || 'MXN').toUpperCase()
                  return (
                    <tr key={pid}>
                      <td>#{pid}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={mon}
                          onChange={(e) => updatePago(pid, 'moneda', e.target.value)}
                          style={{ maxWidth: 140 }}
                        >
                          {(catalogoMonedas?.length
                            ? catalogoMonedas
                            : [{ codigo: 'MXN' }, { codigo: 'USD' }]
                          ).map((m) => (
                            <option key={m.codigo} value={m.codigo}>
                              {m.codigo}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={0}
                          step="0.000001"
                          disabled={mon === 'MXN'}
                          value={mon === 'MXN' ? '1' : (p.tipo_cambio ?? '')}
                          onChange={(e) => updatePago(pid, 'tipo_cambio', e.target.value)}
                          style={{ maxWidth: 140 }}
                          placeholder="MXN / 1"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>

            <Form.Label>Aplicaciones a documentar *</Form.Label>
            <Table size="sm" bordered responsive>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Pago</th>
                  <th>Importe pagado</th>
                  <th>Parcialidad</th>
                  <th>Saldo ant.</th>
                  <th>Saldo insoluto</th>
                  <th>EquivalenciaDR</th>
                </tr>
              </thead>
              <tbody>
                {aplicaciones.map((a) => {
                  const checked = selectedAplicacionIds.includes(a.id)
                  const linea = lineasById[a.id] || {}
                  const monP = (pagosById[a.pago?.id]?.moneda || a.pago?.moneda || 'MXN').toUpperCase()
                  const mismaMoneda = monP === monedaDr
                  return (
                    <tr key={a.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleAplicacion(a.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <small>
                          #{a.pago?.id} — {a.pago?.payment_date}
                          {a.pago?.payment_method ? ` (${a.pago.payment_method})` : ''}
                          <br />
                          {monP} → {monedaDr}
                        </small>
                      </td>
                      <td>{formatCurrency(a.monto || 0)}</td>
                      <td>
                        <Form.Control
                          type="number"
                          min={1}
                          step={1}
                          size="sm"
                          disabled={!checked}
                          value={linea.num_parcialidad ?? ''}
                          onChange={(e) => updateLinea(a.id, 'num_parcialidad', parseInt(e.target.value, 10) || 1)}
                          style={{ maxWidth: 72 }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          size="sm"
                          disabled={!checked}
                          value={linea.imp_saldo_ant ?? ''}
                          onChange={(e) => updateLinea(a.id, 'imp_saldo_ant', e.target.value)}
                          style={{ maxWidth: 100 }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          size="sm"
                          disabled={!checked}
                          value={linea.imp_saldo_insoluto ?? ''}
                          onChange={(e) => updateLinea(a.id, 'imp_saldo_insoluto', e.target.value)}
                          style={{ maxWidth: 100 }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.000001"
                          size="sm"
                          disabled={!checked || mismaMoneda}
                          value={mismaMoneda ? '1' : (linea.equivalencia_dr ?? '')}
                          onChange={(e) => updateLinea(a.id, 'equivalencia_dr', e.target.value)}
                          style={{ maxWidth: 110 }}
                          title={a.equivalencia_dr_sugerida ? `Sugerida: ${a.equivalencia_dr_sugerida}` : ''}
                        />
                        {!mismaMoneda && a.equivalencia_dr_sugerida && (
                          <Form.Text className="d-block text-muted" style={{ fontSize: 10 }}>
                            Sug: {a.equivalencia_dr_sugerida}
                          </Form.Text>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit || createMutation.isLoading || !!complementoBorradorId || aplicaciones.length === 0}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creando borrador…
            </>
          ) : esMultiPago ? (
            'Generar REP multi-pago (borrador)'
          ) : (
            'Generar complemento (borrador)'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ComplementoPagoModal
